// src/hooks/useTheme.js
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "ui.theme";
const ATTR = "data-theme";

const readStoredTheme = () => {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
};

const readSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const initialTheme = () => readStoredTheme() || readSystemTheme();

/**
 * Тема интерфейса: 'light' | 'dark'.
 *
 * Источник правды - атрибут data-theme на <html>. React-состояние
 * зеркалит его, чтобы компоненты могли рендерить разные иконки и
 * aria-состояния. Если атрибут уже выставлен inline-скриптом в
 * index.html (см. antiFOUC), при первом рендере тема совпадает с
 * ним и вспышки светлой темы не происходит.
 *
 * Стор: без внешнего стора - useState в одной точке вызова. Если
 * понадобится второй потребитель (например, тост «тема переключена»),
 * надо вынести в контекст или small store: useState не синхронизирует
 * несколько экземпляров хука.
 */
export function useTheme() {
  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute(ATTR, theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* приватный режим */
    }
  }, [theme]);

  // Если пользователь поменял системную тему - следуем за ней,
  // пока он не сделал явный выбор через тумблер (то есть пока в LS пусто).
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      if (readStoredTheme()) return;
      setTheme(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggle, setTheme, isDark: theme === "dark" };
}
